import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Provider } from "./Context";
import { Spinner, Container } from "react-bootstrap";

// Ladataan heti tärkeimmät sivut (Layout, Home, NoPage)
import Layout from "./pages/Layout";
import Home from "./pages/Home";
import NoPage from "./pages/NoPage";

// Lazy-ladataan kaikki muut sivut
const Blogs = lazy(() => import("./pages/Blogs"));
const Courses = lazy(() => import("./pages/Courses"));
const Julkaisut = lazy(() => import("./pages/Julkaisut"));
const BlogPage = lazy(() => import("./BlogPage"));

// RF-kurssisivut
const Radiotekniikka = lazy(() => import("./pages/RF/Radiotekniikka"));
const Siirtolinjat = lazy(() => import("./pages/RF/Siirtolinjat"));
const Emaallot = lazy(() => import("./pages/RF/Emaallot"));
const Antennit = lazy(() => import("./pages/RF/Antennit"));
const Sparametrit = lazy(() => import("./pages/RF/Sparametrit"));
const SparametriKetjutus = lazy(() => import("./pages/RF/SparametriKetjutus"));
const Kohina = lazy(() => import("./pages/RF/Kohina"));
const Smith = lazy(() => import("./pages/RF/Smith"));
const SmithTeoria = lazy(() => import("./pages/RF/SmithTeoria"));
const Sovitus = lazy(() => import("./pages/RF/Sovitus"));
const Skinsyvyys = lazy(() => import("./pages/RF/Skinsyvyys"));
const RFtekniikka = lazy(() => import("./pages/RF/RFtekniikka"));
const SWR = lazy(() => import("./pages/RF/SWR"));
const Antenniarrayt = lazy(() => import("./pages/RF/Antenniarrayt"));
const Antennimittaukset = lazy(() => import("./pages/RF/Antennimittaukset"));
const RadioKomponentit = lazy(() => import("./pages/RF/RadioKomponentit"));
const Moodit = lazy(() => import("./pages/RF/Moodit"));

// Data Analysis -kurssisivut
const IntroDataAnalysis = lazy(() => import("./pages/DataAnalysis/IntroDataAnalysis"));
const DataAnalysis = lazy(() => import("./pages/DataAnalysis/DataAnalysis"));
const IntroVisualization = lazy(() => import("./pages/DataAnalysis/IntroVisualization"));
const DataVisualizationTheory = lazy(() => import("./pages/DataAnalysis/DataVisualizationTheory"));
const VisualPerception = lazy(() => import("./pages/DataAnalysis/VisualPerception"));
const ColorTheory = lazy(() => import("./pages/DataAnalysis/ColorTheory"));
const ChartTypes = lazy(() => import("./pages/DataAnalysis/ChartTypes"));
const TimeSeriesRegression = lazy(() => import("./pages/DataAnalysis/TimeSeriesRegression"));
const Maps = lazy(() => import("./pages/DataAnalysis/Maps"));
const NetworkVisualization = lazy(() => import("./pages/DataAnalysis/NetworkVisualization"));
const DataAnalysisProcess = lazy(() => import("./pages/DataAnalysis/DataAnalysisProcess"));
const PythonDataAnalysis = lazy(() => import("./pages/DataAnalysis/PythonDataAnalysis"));
const StatisticalAnalysis = lazy(() => import("./pages/DataAnalysis/StatisticalAnalysis"));
const SupervisedMLRegression = lazy(() => import("./pages/DataAnalysis/SupervisedMLRegression"));
const SupervisedMLClassification = lazy(() => import("./pages/DataAnalysis/SupervisedMLClassification"));
const UnsupervisedMLClustering = lazy(() => import("./pages/DataAnalysis/UnsupervisedMLClustering"));
const UnsupervisedMLFactor = lazy(() => import("./pages/DataAnalysis/UnsupervisedMLFactor"));
const BigDataAnalytics = lazy(() => import("./pages/DataAnalysis/BigDataAnalytics"));
const TextMining = lazy(() => import("./pages/DataAnalysis/TextMining"));
const VisualizationTools = lazy(() => import("./pages/DataAnalysis/VisualizationTools"));
const Correlation = lazy(() => import("./pages/DataAnalysis/Correlation"));

// Cloud Services -kurssisivut
const CloudServices = lazy(() => import("./pages/CloudServices/CloudServices"));
const CloudServicesWeek1 = lazy(() => import("./pages/CloudServices/CloudServicesWeek1"));
const CloudServicesWeek2 = lazy(() => import("./pages/CloudServices/CloudServicesWeek2"));
const CloudServicesWeek3 = lazy(() => import("./pages/CloudServices/CloudServicesWeek3"));
const CloudServicesWeek4 = lazy(() => import("./pages/CloudServices/CloudServicesWeek4"));
const CloudServicesWeek5 = lazy(() => import("./pages/CloudServices/CloudServicesWeek5"));
const CloudServicesWeek6 = lazy(() => import("./pages/CloudServices/CloudServicesWeek6"));
const CloudServicesWeek7 = lazy(() => import("./pages/CloudServices/CloudServicesWeek7"));
const CloudServicesIacSetup = lazy(() => import("./pages/CloudServices/CloudServicesIacSetup"));
const CloudServicesVPSSetup = lazy(() => import("./pages/CloudServices/CloudServicesVPSSetup"));
const CloudServicesRahtiSetup = lazy(() => import("./pages/CloudServices/CloudServicesRahtiSetup"));
const CloudServicesMqttSetup = lazy(() => import("./pages/CloudServices/CloudServicesMqttSetup"));
const CloudServicesMqttCscSetup = lazy(() => import("./pages/CloudServices/CloudServicesMqttCscSetup"));

// Ohjelmointi-kurssisivut
const Silmukat = lazy(() => import("./pages/Ohjelmointi/Silmukat"));
const Ckieli = lazy(() => import("./pages/Ohjelmointi/Ckieli"));

// Mobile Programming -kurssisivut
const Kotlin = lazy(() => import("./pages/MobileProgramming/Kotlin"));
const MobileProgrammingKurssi = lazy(() => import("./pages/MobileProgramming/MobileProgrammingKurssi"));
const AndroidKotlin = lazy(() => import("./pages/MobileProgramming/AndroidKotlin"));
const AndroidUI = lazy(() => import("./pages/MobileProgramming/AndroidUI"));
const AndroidState = lazy(() => import("./pages/MobileProgramming/AndroidState"));
const AndroidLifecycle = lazy(() => import("./pages/MobileProgramming/AndroidLifecycle"));
const AndroidNavigation = lazy(() => import("./pages/MobileProgramming/AndroidNavigation"));
const AndroidNetwork = lazy(() => import("./pages/MobileProgramming/AndroidNetwork"));
const AndroidDatabase = lazy(() => import("./pages/MobileProgramming/AndroidDatabase"));
const AndroidGoogleAuth = lazy(() => import("./pages/MobileProgramming/AndroidGoogleAuth"));
const AndroidAdvanced = lazy(() => import("./pages/MobileProgramming/AndroidAdvanced"));
const AndroidViewModel = lazy(() => import("./pages/MobileProgramming/AndroidViewModel"));
const LuontopeliKurssi = lazy(() => import("./pages/MobileProgramming/LuontopeliKurssi"));
const LuontopeliViikko1 = lazy(() => import("./pages/MobileProgramming/LuontopeliViikko1"));
const LuontopeliViikko2 = lazy(() => import("./pages/MobileProgramming/LuontopeliViikko2"));
const LuontopeliViikko3 = lazy(() => import("./pages/MobileProgramming/LuontopeliViikko3"));
const LuontopeliViikko4 = lazy(() => import("./pages/MobileProgramming/LuontopeliViikko4"));
const LuontopeliViikko5 = lazy(() => import("./pages/MobileProgramming/LuontopeliViikko5"));
const LuontopeliViikko6 = lazy(() => import("./pages/MobileProgramming/LuontopeliViikko6"));
const LuontopeliViikko7 = lazy(() => import("./pages/MobileProgramming/LuontopeliViikko7"));
const GpsTrackerOpas = lazy(() => import("./pages/MobileProgramming/GpsTrackerOpas"));
const KameraHarjoitus = lazy(() => import("./pages/MobileProgramming/KameraHarjoitus"));
const TensorFlowHarjoitus = lazy(() => import("./pages/MobileProgramming/TensorFlowHarjoitus"));

// Linux-kurssisivut
const Linuxkurssi = lazy(() => import("./pages/Linux/Linuxkurssi"));
const LinuxKomentorivi = lazy(() => import("./pages/Linux/LinuxKomentorivi"));
const LinuxProsessit = lazy(() => import("./pages/Linux/LinuxProsessit"));
const LinuxKayttajat = lazy(() => import("./pages/Linux/LinuxKayttajat"));
const LinuxPaketinhallinta = lazy(() => import("./pages/Linux/LinuxPaketinhallinta"));
const LinuxTiedostojarjestelma = lazy(() => import("./pages/Linux/LinuxTiedostojarjestelma"));
const LinuxBash = lazy(() => import("./pages/Linux/LinuxBash"));
const LinuxDotfiles = lazy(() => import("./pages/Linux/LinuxDotfiles"));
const LinuxSSH = lazy(() => import("./pages/Linux/LinuxSSH"));
const LinuxCron = lazy(() => import("./pages/Linux/LinuxCron"));
const LinuxPortit = lazy(() => import("./pages/Linux/LinuxPortit"));
const LinuxCORS = lazy(() => import("./pages/Linux/LinuxCORS"));
const LinuxCopilot = lazy(() => import("./pages/Linux/LinuxCopilot"));
const LinuxVerkko = lazy(() => import("./pages/Linux/LinuxVerkko"));
const LinuxHallinta = lazy(() => import("./pages/Linux/LinuxHallinta"));
const LinuxKontit = lazy(() => import("./pages/Linux/LinuxKontit"));
const LinuxKubernetes = lazy(() => import("./pages/Linux/LinuxKubernetes"));
const LinuxCICD = lazy(() => import("./pages/Linux/LinuxCICD"));
const LinuxYAML = lazy(() => import("./pages/Linux/LinuxYAML"));
const LinuxEditorit = lazy(() => import("./pages/Linux/LinuxEditorit"));
const LinuxKaynnistys = lazy(() => import("./pages/Linux/LinuxKaynnistys"));
const LinuxTekstinkasittely = lazy(() => import("./pages/Linux/LinuxTekstinkasittely"));
const LinuxLevyt = lazy(() => import("./pages/Linux/LinuxLevyt"));
const LinuxVianetsinta = lazy(() => import("./pages/Linux/LinuxVianetsinta"));
const LinuxTietoturva = lazy(() => import("./pages/Linux/LinuxTietoturva"));
const LinuxNginx = lazy(() => import("./pages/Linux/LinuxNginx"));
const LinuxTietokannat = lazy(() => import("./pages/Linux/LinuxTietokannat"));

// Digitaalitekniikka-kurssisivut
const Digitaali = lazy(() => import("./pages/Digitaali/Digitaali"));
const DigitaaliHistoria = lazy(() => import("./pages/Digitaali/DigitaaliHistoria"));
const DigitaaliKombinaatiopiirit = lazy(() => import("./pages/Digitaali/DigitaaliKombinaatiopiirit"));

// Projektit-sivut
const Projektit = lazy(() => import("./pages/Projektit/Projektit"));
const RecoveryTester = lazy(() => import("./pages/Projektit/RecoveryTester"));
const RecoveryTesterPrivacyPolicy = lazy(() => import("./pages/Projektit/RecoveryTesterPrivacyPolicy"));
const Sukututkimus = lazy(() => import("./pages/Projektit/Sukututkimus"));
const TekstiTV = lazy(() => import("./pages/Projektit/TekstiTV"));
const OneRepMax = lazy(() => import("./pages/Projektit/OneRepMax"));

// Muut sivut
const Wedding = lazy(() => import("./pages/Wedding/Wedding"));

// Latauskomponentti
const LoadingFallback = () => (
  <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
    <div className="text-center">
      <Spinner animation="border" role="status" variant="primary">
        <span className="visually-hidden">Ladataan...</span>
      </Spinner>
      <p className="mt-3 text-muted">Ladataan sivua...</p>
    </div>
  </Container>
);

export default function App() {
  return (
    <Provider>
      <BrowserRouter>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={<Layout />}>
              <Route index element={<Home />} />
              <Route path="blogs" element={<Blogs />} />
              <Route path="courses" element={<Courses />} />
              <Route path="julkaisut" element={<Julkaisut />} />
              <Route path="radiotekniikka" element={<Radiotekniikka />} />
              <Route path="siirtolinjat" element={<Siirtolinjat />} />
              <Route path="emaallot" element={<Emaallot />} />
              <Route path="antennit" element={<Antennit />} />
              <Route path="antenniarrayt" element={<Antenniarrayt />} />
              <Route path="antennimittaukset" element={<Antennimittaukset />} />
              <Route path="radiokomponentit" element={<RadioKomponentit />} />
              <Route path="moodit" element={<Moodit />} />
              <Route path="sparametrit" element={<Sparametrit />} />
              <Route path="sparametriketjutus" element={<SparametriKetjutus />} />
              <Route path="kohina" element={<Kohina />} />
              <Route path="smith" element={<Smith />} />
              <Route path="smithteoria" element={<SmithTeoria />} />
              <Route path="ckieli" element={<Ckieli />} />
              <Route path="silmukat" element={<Silmukat />} />
              <Route path="sovitus" element={<Sovitus />} />
              <Route path="introdataanalysis" element={<IntroDataAnalysis />} />
              <Route path="dataanalysis" element={<DataAnalysis />} />
              <Route path="introvisualization" element={<IntroVisualization />} />
              <Route path="datavisualizationtheory" element={<DataVisualizationTheory />} />
              <Route path="visualperception" element={<VisualPerception />} />
              <Route path="colortheory" element={<ColorTheory />} />
              <Route path="charttypes" element={<ChartTypes />} />
              <Route path="timeseriesregression" element={<TimeSeriesRegression />} />
              <Route path="maps" element={<Maps />} />
              <Route path="networkvisualization" element={<NetworkVisualization />} />
              <Route path="dataanalysisprocess" element={<DataAnalysisProcess />} />
              <Route path="pythondataanalysis" element={<PythonDataAnalysis />} />
              <Route path="statisticalanalysis" element={<StatisticalAnalysis />} />
              <Route path="supervisedmlregression" element={<SupervisedMLRegression />} />
              <Route path="supervisedmlclassification" element={<SupervisedMLClassification />} />
              <Route path="unsupervisedmlclustering" element={<UnsupervisedMLClustering />} />
              <Route path="unsupervisedmlfactor" element={<UnsupervisedMLFactor />} />
              <Route path="bigdataanalytics" element={<BigDataAnalytics />} />
              <Route path="textmining" element={<TextMining />} />
              <Route path="visualizationtools" element={<VisualizationTools />} />
              <Route path="correlation" element={<Correlation />} />
              <Route path="cloudservices" element={<CloudServices />} />
              <Route path="cloudservicesweek1" element={<CloudServicesWeek1 />} />
              <Route path="cloudservicesweek2" element={<CloudServicesWeek2 />} />
              <Route path="cloudservicesweek3" element={<CloudServicesWeek3 />} />
              <Route path="cloudservicesweek4" element={<CloudServicesWeek4 />} />
              <Route path="cloudservicesweek5" element={<CloudServicesWeek5 />} />
              <Route path="cloudservicesweek6" element={<CloudServicesWeek6 />} />
              <Route path="cloudservicesweek7" element={<CloudServicesWeek7 />} />
              <Route path="cloudservicesiacsetup" element={<CloudServicesIacSetup />} />
              <Route path="cloudservicesvpssetup" element={<CloudServicesVPSSetup />} />
              <Route path="cloudservicesrahtisetup" element={<CloudServicesRahtiSetup />} />
              <Route path="cloudservicesmqttsetup" element={<CloudServicesMqttSetup />} />
              <Route path="cloudservicesmqttcscsetup" element={<CloudServicesMqttCscSetup />} />
              <Route path="kotlin" element={<Kotlin />} />
              <Route path="mobileprogramming" element={<MobileProgrammingKurssi />} />
              <Route path="androidkotlin" element={<AndroidKotlin />} />
              <Route path="androidui" element={<AndroidUI />} />
              <Route path="androidstate" element={<AndroidState />} />
              <Route path="androidlifecycle" element={<AndroidLifecycle />} />
              <Route path="androidnavigation" element={<AndroidNavigation />} />
              <Route path="androidnetwork" element={<AndroidNetwork />} />
              <Route path="androiddatabase" element={<AndroidDatabase />} />
              <Route path="androidgoogleauth" element={<AndroidGoogleAuth />} />
              <Route path="androidadvanced" element={<AndroidAdvanced />} />
              <Route path="androidviewmodel" element={<AndroidViewModel />} />
              <Route path="luontopeli" element={<LuontopeliKurssi />} />
              <Route path="luontopeliviikko1" element={<LuontopeliViikko1 />} />
              <Route path="luontopeliviikko2" element={<LuontopeliViikko2 />} />
              <Route path="luontopeliviikko3" element={<LuontopeliViikko3 />} />
              <Route path="luontopeliviikko4" element={<LuontopeliViikko4 />} />
              <Route path="luontopeliviikko5" element={<LuontopeliViikko5 />} />
              <Route path="luontopeliviikko6" element={<LuontopeliViikko6 />} />
              <Route path="luontopeliviikko7" element={<LuontopeliViikko7 />} />
              <Route path="gpstrackeropas" element={<GpsTrackerOpas />} />
              <Route path="kameraharjoitus" element={<KameraHarjoitus />} />
              <Route path="tensorflowharjoitus" element={<TensorFlowHarjoitus />} />
              <Route path="linuxkurssi" element={<Linuxkurssi />} />
              <Route path="linuxkomentorivi" element={<LinuxKomentorivi />} />
              <Route path="linuxprosessit" element={<LinuxProsessit />} />
              <Route path="linuxkayttajat" element={<LinuxKayttajat />} />
              <Route path="linuxpaketinhallinta" element={<LinuxPaketinhallinta />} />
              <Route path="linuxtiedostojarjestelma" element={<LinuxTiedostojarjestelma />} />
              <Route path="linuxbash" element={<LinuxBash />} />
              <Route path="linuxdotfiles" element={<LinuxDotfiles />} />
              <Route path="linuxssh" element={<LinuxSSH />} />
              <Route path="linuxcron" element={<LinuxCron />} />
              <Route path="linuxportit" element={<LinuxPortit />} />
              <Route path="linuxcors" element={<LinuxCORS />} />
              <Route path="linuxcopilot" element={<LinuxCopilot />} />
              <Route path="linuxverkko" element={<LinuxVerkko />} />
              <Route path="linuxhallinta" element={<LinuxHallinta />} />
              <Route path="linuxkontit" element={<LinuxKontit />} />
              <Route path="linuxkubernetes" element={<LinuxKubernetes />} />
              <Route path="linuxcicd" element={<LinuxCICD />} />
              <Route path="linuxyaml" element={<LinuxYAML />} />
              <Route path="linuxeditorit" element={<LinuxEditorit />} />
              <Route path="linuxkaynnistys" element={<LinuxKaynnistys />} />
              <Route path="linuxtekstinkasittely" element={<LinuxTekstinkasittely />} />
              <Route path="linuxlevyt" element={<LinuxLevyt />} />
              <Route path="linuxvianetsinta" element={<LinuxVianetsinta />} />
              <Route path="linuxtietoturva" element={<LinuxTietoturva />} />
              <Route path="linuxnginx" element={<LinuxNginx />} />
              <Route path="linuxtietokannat" element={<LinuxTietokannat />} />
              <Route path="skinsyvyys" element={<Skinsyvyys />} />
              <Route path="rftekniikka" element={<RFtekniikka />} />
              <Route path="swr" element={<SWR />} />
              <Route path="digitaali" element={<Digitaali />} />
              <Route path="digitaalihistoria" element={<DigitaaliHistoria />} />
              <Route path="digitaalikombinaatiopiirit" element={<DigitaaliKombinaatiopiirit />} />
              <Route path="riiajaville" element={<Wedding />} />
              <Route path="projektit" element={<Projektit />} />
              <Route path="recoverytester" element={<RecoveryTester />} />
              <Route path="recoverytester/privacypolicy" element={<RecoveryTesterPrivacyPolicy />} />
              <Route path="sukututkimus" element={<Sukututkimus />} />
              <Route path="tekstitv" element={<TekstiTV />} />
              <Route path="1rm" element={<OneRepMax />} />
              <Route path="blogs" element={<Blogs />}>
                <Route path=":name" element={<BlogPage />} />
              </Route>
              <Route path="*" element={<NoPage />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </Provider>
  );
}
